"use client";

import { useState } from "react";
import ErrorBoundary from "rivals/components/pages/Article/ErrorBoundary";
import InfiniteAd from "rivals/components/shared/Ads/Benji/InfiniteAd";
import {
  ImageBlock as ApiImageBlock,
  BlockType
} from "rivals/shared/interfaces/Content";
import AthleteBlock from "./AthleteBlock";
import DividerBlock from "./DividerBlock";
import GalleryBlock from "./GalleryBlock/container";
import HeadingBlock from "./HeadingBlock";
import ImageBlock from "./ImageBlock";
import InstagramBlock from "./InstagramBlock/container";
import LightBox from "./LightBox";
import { LightBoxContextProvider } from "./LightBox/context";
import ListBlock from "./ListBlock/container";
import QuoteBlock from "./QuoteBlock";
import TableBlock from "./TableBlock";
import TemplateBlock from "./TemplateBlock/container";
import TextBlock from "./TextBlock";
import TweetBlock from "./TweetBlock/container";
import VideoBlock from "./VideoBlock/container";
import type { Props } from "./types";

function Blocks({
  blocks,
  contentSpaceId,
  index
}: Props): React.JSX.Element | null {
  const [brightcoveVideosOnScreen, setBrightcoveVideosOnScreen] = useState<
    string[]
  >([]);
  if (!blocks?.length) {
    return null;
  }

  const imageBlocks = blocks.filter(
    block => block.type === BlockType.Image
  ) as ApiImageBlock[];

  const hasImageBlocks = imageBlocks.length > 0;

  // Using a imageIndex so we can open the images lightbox in the correct slide
  let imageIndex = 0;

  return (
    <LightBoxContextProvider>
      {hasImageBlocks && <LightBox imageBlocks={imageBlocks} />}
      {blocks.map((block, idx) => {
        const key = `${index}-${idx}`;

        switch (block.type) {
          case BlockType.Ad:
            return <InfiniteAd key={key} size={"small"} slotType="lrec" />;
          case BlockType.Athlete:
            // Ensure we have the required data to render the AthleteBlock
            if (block.data.id && block.data.playerType) {
              return (
                <ErrorBoundary key={key} withSuspense>
                  <AthleteBlock
                    athleteSlug={block.data.athleteSlug}
                    id={block.data.id.toString()}
                    type={block.data.playerType}
                  />
                </ErrorBoundary>
              );
            } else {
              return null;
            }
          case BlockType.Divider:
            return <DividerBlock key={key} />;
          case BlockType.Gallery:
            return (
              <ErrorBoundary key={key} withSuspense>
                <GalleryBlock id={parseInt(block.data.id)} />
              </ErrorBoundary>
            );
          case BlockType.Heading:
            return <HeadingBlock key={key} text={block.data.text} />;
          case BlockType.Image:
            // Bumping the image index
            imageIndex++;
            return (
              <ImageBlock
                alt={block.data.alt}
                caption={block.data.caption}
                credit={block.data.credit}
                imageIndex={imageIndex - 1}
                key={key}
                src={block.data.largeUrl}
                url={block.data.url}
              />
            );
          case BlockType.Instagram:
            return (
              <ErrorBoundary key={key} withSuspense>
                <InstagramBlock html={block.data.html} />
              </ErrorBoundary>
            );
          case BlockType.List:
            return (
              <ErrorBoundary key={key} withSuspense>
                <ListBlock
                  key={key}
                  listId={block.data.id}
                  listType={block.data.listType}
                />
              </ErrorBoundary>
            );
          case BlockType.Quote: {
            return (
              <QuoteBlock
                author={block.data.credit}
                key={key}
                quote={block.data.text}
              />
            );
          }
          case BlockType.Template:
            return (
              <ErrorBoundary key={key} withSuspense>
                <TemplateBlock {...block.data} />
              </ErrorBoundary>
            );
          case BlockType.Text:
            return <TextBlock html={block.data.text} key={key} />;
          case BlockType.Table:
            return <TableBlock key={key} {...block.data} />;
          case BlockType.Tweet:
            return (
              <ErrorBoundary key={key} withSuspense>
                <TweetBlock html={block.data.html} />
              </ErrorBoundary>
            );
          case BlockType.Video:
            return (
              <ErrorBoundary key={key} withSuspense>
                <VideoBlock
                  autoplay={block.data.autoplay}
                  blockId={key}
                  brightcoveVideosOnScreen={brightcoveVideosOnScreen}
                  contentSpaceId={contentSpaceId}
                  currentBrightcoveVideoBlockId={brightcoveVideosOnScreen[0]}
                  remoteId={block.data.remoteId}
                  setBrightcoveVideosOnScreen={setBrightcoveVideosOnScreen}
                  source={block.data.source}
                />
              </ErrorBoundary>
            );
          default:
            return null;
        }
      })}
    </LightBoxContextProvider>
  );
}

export default Blocks;
